import { useCallback, useEffect, useState } from 'react';
import type { Explanation } from './UserOperationModesPanel';
import { UserOperationModesPanel } from './UserOperationModesPanel';
import { Button } from '@framework/ui/atoms/Button';
import { GeneralModal } from '@framework/ui/elements/GeneralModal';
import type { UserOperationModes } from '@user/pages/ViewModelPage/UserOperationModesProvider';
import { useUserOperationModes } from '@user/pages/ViewModelPage/UserOperationModesProvider';
import { useTranslation } from 'react-i18next';
type Props = {
  isOpen: boolean;
  onClose(): void;
};

export const UserOperationModesModal: React.FC<Props> = ({ isOpen, onClose }: Props) => {
  const { t } = useTranslation();
  const { userOperationMode, modeSwitcher } = useUserOperationModes();
  const [selectedMode, setSelectedMode] = useState<UserOperationModes | null>(null);

  const explanationMap: Explanation = {
    mouse: {
      move: t('canvas.operationMode.explanation.dragMove'),
      select: t('canvas.operationMode.explanation.shiftDragSelect'),
    },
    trackpad: {
      move: t('canvas.operationMode.explanation.dragMove'),
      select: t('canvas.operationMode.explanation.shiftDragSelect'),
    },
  };

  const explanationSlide: Explanation = {
    mouse: {
      move: t('canvas.operationMode.explanation.rightClickMove'),
      select: t('canvas.operationMode.explanation.dragSelect'),
    },
    trackpad: {
      move: t('canvas.operationMode.explanation.scrollMove'),
      select: t('canvas.operationMode.explanation.dragSelect'),
    },
  };

  useEffect(() => {
    setSelectedMode(userOperationMode);
  }, [userOperationMode]);

  const handleClose = useCallback(() => {
    // 保存しなかった場合、DBの値に戻す
    setSelectedMode(userOperationMode);
    onClose();
  }, [onClose, userOperationMode]);

  const handleSubmit = useCallback(() => {
    if (selectedMode !== null) {
      modeSwitcher(selectedMode);
    }
    onClose();
  }, [modeSwitcher, selectedMode, onClose]);

  const tempModeSwitcher = useCallback(
    (mode: UserOperationModes) => {
      if (mode === selectedMode) return;

      setSelectedMode(selectedMode === 'map' ? 'slide' : 'map');
    },
    [selectedMode]
  );

  return (
    <GeneralModal
      isOpen={isOpen}
      onClose={handleClose}
      submitButton={
        <Button color="primary" onClick={handleSubmit}>
          {t('common.action.save')}
        </Button>
      }
      cancelButton={
        <Button color="secondary" onClick={handleClose}>
          {t('common.action.cancel')}
        </Button>
      }
      title={t('viewModel.operationMode.label')}
    >
      <div className="flex flex-row p-5">
        <UserOperationModesPanel
          title={t('canvas.operationMode.modal.mapMode')}
          explanation={explanationMap}
          isSelected={selectedMode === 'map'}
          mode="map"
          tempModeSwitcher={tempModeSwitcher}
        />
        <UserOperationModesPanel
          title={t('canvas.operationMode.modal.slideMode')}
          explanation={explanationSlide}
          isSelected={selectedMode === 'slide'}
          mode="slide"
          tempModeSwitcher={tempModeSwitcher}
        />
      </div>
    </GeneralModal>
  );
};
